import { useEffect, useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";
import { useValidateEmailMutation } from "../../../store/api/authApi";

interface BaseFormData {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	password: string;
	agreeToTerms: boolean;
}

type FormData = BaseFormData & Record<string, any>;

interface BasicInfoStepProps {
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	errors: Record<string, string>;
	setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
	formData,
	setFormData,
	errors,
	setErrors,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [validateEmail, { isLoading: isValidatingEmail }] =
		useValidateEmailMutation();
	const [emailValidationStatus, setEmailValidationStatus] = useState<{
		status: "idle" | "validating" | "valid" | "invalid";
		message?: string;
	}>({ status: "idle" });
	const emailValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const handleInputChange = (
		field: keyof FormData,
		value: string | boolean,
	) => {
		// Normalize email to lowercase
		const normalizedValue =
			field === "email" && typeof value === "string"
				? value.toLowerCase()
				: value;
		setFormData((prev: FormData) => ({ ...prev, [field]: normalizedValue }));
		if (errors[field]) {
			setErrors((prev: Record<string, string>) => ({
				...prev,
				[field]: "",
			}));
		}

		// Real-time email validation with debouncing
		if (field === "email" && typeof normalizedValue === "string") {
			// Clear previous timeout
			if (emailValidationTimeoutRef.current) {
				clearTimeout(emailValidationTimeoutRef.current);
			}

			// Reset validation status if email is empty
			if (!normalizedValue.trim()) {
				setEmailValidationStatus({ status: "idle" });
				return;
			}

			// Basic format check first
			if (!/\S+@\S+\.\S+/.test(normalizedValue)) {
				setEmailValidationStatus({ status: "idle" });
				return;
			}

			// Set validating status
			setEmailValidationStatus({ status: "validating" });

			// Debounce API call (wait 800ms after user stops typing)
			emailValidationTimeoutRef.current = setTimeout(async () => {
				try {
					const normalizedEmail = normalizedValue.trim().toLowerCase();
					const result = await validateEmail({
						email: normalizedEmail,
					}).unwrap();
					if (result.valid) {
						setEmailValidationStatus({
							status: "valid",
							message: result.message,
						});
						// Clear any email errors
						if (errors.email) {
							setErrors((prev) => ({ ...prev, email: "" }));
						}
					} else {
						setEmailValidationStatus({
							status: "invalid",
							message: result.message,
						});
					}
				} catch (error: any) {
					// On error, don't block the user - just reset to idle
					setEmailValidationStatus({ status: "idle" });
				}
			}, 800);
		}
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (emailValidationTimeoutRef.current) {
				clearTimeout(emailValidationTimeoutRef.current);
			}
		};
	}, []);

	const getPasswordStrength = (password: string) => {
		if (!password) return { strength: 0, text: "", color: "" };

		let score = 0;
		if (password.length >= 8) score++;
		if (/[A-Z]/.test(password)) score++;
		if (/[a-z]/.test(password)) score++;
		if (/[0-9]/.test(password)) score++;
		if (/[^A-Za-z0-9]/.test(password)) score++;

		const levels = [
			{ strength: 0, text: "Very Weak", color: "bg-error" },
			{ strength: 1, text: "Weak", color: "bg-error" },
			{ strength: 2, text: "Fair", color: "bg-warning" },
			{ strength: 3, text: "Good", color: "bg-warning" },
			{ strength: 4, text: "Strong", color: "bg-success" },
			{ strength: 5, text: "Very Strong", color: "bg-success" },
		];

		return levels[score];
	};

	const passwordStrength = getPasswordStrength(formData.password);

	return (
		<div className="space-y-6">
			<div className="text-center mb-8">
				<div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
					<Icon name="User" size={24} />
				</div>
				<h2 className="text-2xl font-semibold text-foreground mb-2">
					Basic Information
				</h2>
				<p className="text-muted-foreground">
					Let&apos;s start with your personal details
				</p>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<Input
					label="First Name"
					type="text"
					placeholder="Enter your first name"
					value={formData.firstName}
					onChange={(e) => handleInputChange("firstName", e.target.value)}
					error={errors.firstName}
					required
				/>

				<Input
					label="Last Name"
					type="text"
					placeholder="Enter your last name"
					value={formData.lastName}
					onChange={(e) => handleInputChange("lastName", e.target.value)}
					error={errors.lastName}
					required
				/>
			</div>

			<div className="space-y-2">
				<Input
					label="Email Address"
					type="email"
					placeholder="Enter your email address"
					value={formData.email}
					onChange={(e) => handleInputChange("email", e.target.value)}
					error={
						errors.email ||
						(emailValidationStatus.status === "invalid"
							? emailValidationStatus.message
							: undefined)
					}
					description="We'll use this for account verification and notifications"
					required
					className={emailValidationStatus.status === "valid" ? "pr-10" : ""}
				/>
				{formData.email && (
					<div className="flex items-center gap-2 text-sm">
						{emailValidationStatus.status === "validating" && (
							<>
								<Icon
									name="Loader2"
									size={16}
									className="animate-spin text-primary"
								/>
								<span className="text-muted-foreground">
									Verifying email...
								</span>
							</>
						)}
						{emailValidationStatus.status === "valid" && (
							<>
								<Icon name="CheckCircle2" size={16} className="text-success" />
								<span className="text-success">Email verified</span>
							</>
						)}
						{emailValidationStatus.status === "invalid" &&
							emailValidationStatus.message && (
								<>
									<Icon name="XCircle" size={16} className="text-destructive" />
									<span className="text-destructive">
										{emailValidationStatus.message}
									</span>
								</>
							)}
					</div>
				)}
			</div>

			<Input
				label="Phone Number"
				type="tel"
				placeholder="Enter your phone number"
				value={formData.phone}
				onChange={(e) => handleInputChange("phone", e.target.value)}
				error={errors.phone}
				description="For important account and property updates (optional)"
			/>

			<div className="space-y-2">
				<label className="text-sm font-medium text-foreground">Password</label>
				<div className="relative">
					<Input
						type={showPassword ? "text" : "password"}
						placeholder="Create a strong password"
						value={formData.password}
						onChange={(e) => handleInputChange("password", e.target.value)}
						error={errors.password}
						required
						className="pr-10"
					/>
					<button
						type="button"
						onClick={() => setShowPassword((prev) => !prev)}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
						aria-label={showPassword ? "Hide password" : "Show password"}
					>
						<Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
					</button>
				</div>

				{formData.password && (
					<div className="space-y-2">
						<div className="flex items-center justify-between text-sm">
							<span className="text-muted-foreground">Password Strength:</span>
							<span
								className={`font-medium ${
									passwordStrength.strength >= 4
										? "text-success"
										: passwordStrength.strength >= 2
										? "text-warning"
										: "text-error"
								}`}
							>
								{passwordStrength.text}
							</span>
						</div>
						<div className="w-full bg-muted rounded-full h-2">
							<div
								className={`h-2 rounded-full smooth-transition ${passwordStrength.color}`}
								style={{
									width: `${(passwordStrength.strength / 5) * 100}%`,
								}}
							/>
						</div>
						<div className="text-xs text-muted-foreground space-y-1">
							<p>Password should contain:</p>
							<ul className="list-disc list-inside space-y-1 ml-2">
								<li
									className={
										formData.password.length >= 8
											? "text-success"
											: "text-muted-foreground"
									}
								>
									At least 8 characters
								</li>
								<li
									className={
										/[A-Z]/.test(formData.password)
											? "text-success"
											: "text-muted-foreground"
									}
								>
									One uppercase letter
								</li>
								<li
									className={
										/[a-z]/.test(formData.password)
											? "text-success"
											: "text-muted-foreground"
									}
								>
									One lowercase letter
								</li>
								<li
									className={
										/[0-9]/.test(formData.password)
											? "text-success"
											: "text-muted-foreground"
									}
								>
									One number
								</li>
								<li
									className={
										/[^A-Za-z0-9]/.test(formData.password)
											? "text-success"
											: "text-muted-foreground"
									}
								>
									One special character
								</li>
							</ul>
						</div>
					</div>
				)}
			</div>

			{/* Terms and Conditions */}
			<div className="pt-4 border-t border-border">
				<label className="flex items-start space-x-3 cursor-pointer">
					<input
						type="checkbox"
						checked={formData.agreeToTerms || false}
						onChange={(e) =>
							handleInputChange("agreeToTerms", e.target.checked)
						}
						className="mt-1 w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
						required
					/>
					<div className="flex-1">
						<span className="text-sm text-foreground">
							I agree to the{" "}
							<a
								href="/terms"
								target="_blank"
								rel="noopener noreferrer"
								className="text-primary hover:text-primary/80 underline"
							>
								Terms of Service
							</a>{" "}
							and{" "}
							<a
								href="/privacy"
								target="_blank"
								rel="noopener noreferrer"
								className="text-primary hover:text-primary/80 underline"
							>
								Privacy Policy
							</a>
						</span>
						{errors.agreeToTerms && (
							<p className="text-sm text-error mt-1">{errors.agreeToTerms}</p>
						)}
					</div>
				</label>
			</div>
		</div>
	);
};

export default BasicInfoStep;
